// GOLD-12178: Sitting state for digit and checkmark TMG. cursorMaxIndex
// is a high-water mark for restore/focus. Checkmark scoring uses each
// cell.reviewed flag so skipped cells stay -1. Restore only when locked
// (Next already persisted).
import {
  INPUT_TYPES,
  createCellsFromSequence,
  findNextEmptyIndex,
  lastReachedIndexFromSubmitted,
  markCellsReviewed,
  parseStoredAnswer,
  parseTimedMatchingGridDetails,
  restoreCellsFromAnswer
} from './timedMatchingGridUtils'

export const ACTIONS = {
  INIT: 'INIT',
  FOCUS_CELL: 'FOCUS_CELL',
  ENTER_DIGIT: 'ENTER_DIGIT',
  CLEAR_CELL: 'CLEAR_CELL',
  TOGGLE_TICK: 'TOGGLE_TICK',
  TICK: 'TICK',
  EXPIRE: 'EXPIRE',
  LOCK: 'LOCK',
  RESTORE: 'RESTORE'
}

// GOLD-12178: Restore saved cells only when the sitting is locked (Next
// already persisted). Expire is not autosaved, so a refresh before Next
// must start a new sitting with a new timer instead of a frozen grid.
export const createInitialState = (
  attributes = {},
  storedAnswer,
  options = {}
) => {
  const locked = !!options.locked
  const config = parseTimedMatchingGridDetails(attributes)
  const restored = locked ? parseStoredAnswer(storedAnswer) : null
  const canRestoreCells =
    !!restored &&
    (Array.isArray(restored.cells) ||
      Array.isArray(restored.submitted_item_sequence))
  const cells = canRestoreCells
    ? restoreCellsFromAnswer(config, restored)
    : createCellsFromSequence(config.itemSequence, config.inputType)

  const restoredCursor = Number(restored?.cursor_max_index)
  const cursorFromSubmitted = lastReachedIndexFromSubmitted(
    restored?.submitted_item_sequence
  )
  const cursorMaxIndex = Number.isFinite(restoredCursor)
    ? restoredCursor
    : Math.max(0, cursorFromSubmitted)
  const focusIndex = Math.min(cursorMaxIndex, Math.max(0, cells.length - 1))
  // The sitting cursor starts on a cell; that cell is already reviewed.
  const reviewedCells = canRestoreCells
    ? cells
    : markCellsReviewed(cells, focusIndex)

  return {
    ...config,
    cells: reviewedCells,
    focusIndex,
    cursorMaxIndex,
    elapsedSeconds: Number.isFinite(Number(restored?.elapsed_seconds))
      ? Number(restored.elapsed_seconds)
      : 0,
    timedOut: !!restored?.timed_out,
    expired: locked || !!restored?.timed_out,
    locked
  }
}

// GOLD-12178: cursorMaxIndex is a high-water mark for restore/focus.
// Scoring uses cell.reviewed, so arrowing back does not un-review a cell
// and jumping ahead does not review skipped cells.
const bumpCursor = (state, index) =>
  Math.max(state.cursorMaxIndex, Number(index) || 0)

export const timedMatchingGridReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.INIT:
      // GOLD-12178: Rebuild from authored attributes (and a locked
      // stored answer) when the question remounts.
      return createInitialState(action.attributes, action.storedAnswer, {
        locked: !!action.locked
      })

    case ACTIONS.RESTORE:
      return {
        ...state,
        ...action.payload
      }

    case ACTIONS.LOCK:
      // GOLD-12178: Lock after Next so Back shows a read-only completed
      // grid instead of restarting the timer.
      if (state.locked && state.expired) return state
      return {
        ...state,
        locked: true,
        expired: true
      }

    case ACTIONS.FOCUS_CELL: {
      if (state.expired || state.locked) return state
      const index = Math.max(
        0,
        Math.min(state.cells.length - 1, Number(action.index) || 0)
      )
      return {
        ...state,
        cells: markCellsReviewed(state.cells, index),
        focusIndex: index,
        cursorMaxIndex: bumpCursor(state, index)
      }
    }

    case ACTIONS.ENTER_DIGIT: {
      // GOLD-12178: Write 1–9, then auto-advance to the next empty cell.
      // Ignore input after expire/lock so a late keystroke cannot change
      // a submitted sitting.
      if (
        state.expired ||
        state.locked ||
        state.inputType !== INPUT_TYPES.digit
      ) {
        return state
      }
      const digit = Number(action.digit)
      if (!Number.isFinite(digit) || digit < 1 || digit > 9) return state
      const focusIndex = Number.isFinite(Number(action.index))
        ? Number(action.index)
        : state.focusIndex
      const cells = state.cells.map(cell =>
        cell.index === focusIndex
          ? {
              ...cell,
              response: digit,
              attempted: true
            }
          : cell
      )
      const nextFocus = findNextEmptyIndex(cells, focusIndex + 1)
      return {
        ...state,
        cells: markCellsReviewed(cells, focusIndex, nextFocus),
        focusIndex: nextFocus,
        cursorMaxIndex: Math.max(state.cursorMaxIndex, focusIndex, nextFocus)
      }
    }

    case ACTIONS.CLEAR_CELL: {
      // GOLD-12178: Backspace/Delete clears the focused digit so the
      // participant can correct a typo without advancing past it.
      if (
        state.expired ||
        state.locked ||
        state.inputType !== INPUT_TYPES.digit
      ) {
        return state
      }
      const focusIndex = Number.isFinite(Number(action.index))
        ? Number(action.index)
        : state.focusIndex
      const cells = state.cells.map(cell =>
        cell.index === focusIndex
          ? {
              ...cell,
              response: undefined,
              attempted: false
            }
          : cell
      )
      return {
        ...state,
        cells: markCellsReviewed(cells, focusIndex),
        focusIndex,
        cursorMaxIndex: bumpCursor(state, focusIndex)
      }
    }

    case ACTIONS.TOGGLE_TICK: {
      // GOLD-12178: Checkmark marks incorrect only via this toggle.
      // Also bumps the cursor so clicking a later checkbox expands the
      // supervised prefix used for scoring.
      if (
        state.expired ||
        state.locked ||
        state.inputType !== INPUT_TYPES.checkmark
      ) {
        return state
      }
      const index = Number.isFinite(Number(action.index))
        ? Number(action.index)
        : state.focusIndex
      const cells = state.cells.map(cell =>
        cell.index === index
          ? {
              ...cell,
              ticked: !cell.ticked,
              attempted: true
            }
          : cell
      )
      return {
        ...state,
        cells: markCellsReviewed(cells, index),
        focusIndex: index,
        cursorMaxIndex: bumpCursor(state, index)
      }
    }

    case ACTIONS.TICK: {
      // GOLD-12178: 1s timer tick. Crossing the limit sets timed_out so
      // submit can tell expire apart from an early Next.
      if (state.expired || state.locked) return state
      const nextElapsed = Math.min(
        state.timeLimitSeconds,
        state.elapsedSeconds + 1
      )
      if (nextElapsed >= state.timeLimitSeconds) {
        return {
          ...state,
          elapsedSeconds: state.timeLimitSeconds,
          expired: true,
          timedOut: true
        }
      }
      return {
        ...state,
        elapsedSeconds: nextElapsed
      }
    }

    case ACTIONS.EXPIRE:
      // GOLD-12178: Force the sitting to the limit. Does not persist
      // until Next; refresh before Next starts a new sitting.
      return {
        ...state,
        elapsedSeconds: state.timeLimitSeconds,
        expired: true,
        timedOut: true
      }

    default:
      return state
  }
}
